import { IRectangle } from '@opengov/components-visualization';
import { createSelector, createStructuredSelector } from '@opengov/compose-app/reselect';
import { BasicColumnTypes } from '@opengov/ogql-types';
import { OG_TARGET_CATEGORY } from '@opengov/reporting-transforms/visualizations';
import {
  EReportChartType,
  EReportColumnTypes,
  EReportTargetValueTypes,
  LegendSortOptions
} from '@opengov/reporting-types';
import { produce } from 'immer';
import { intersection, startCase } from 'lodash';

import { reportLocaleText, selectLocaleFormatter, selectLocalePercentFormat } from '../../../common/locale';
import { reportConfigurationEffects } from '../../../common/reportConfiguration/effects/reportConfigurationEffects';
import {
  selectCustomColorMap,
  selectLegendSortOrder,
  selectPresentationConfiguration,
  selectTarget
} from '../../../common/reportConfiguration/selectors';
import { reportConfigurationActionCreators } from '../../../common/reportConfiguration/state';
import { selectSavedViewEditActiveTabIndex } from '../../../reportSavedViewEdit/selectors/selectReportSavedViewEdit';
import { IExportVisualizationContainerProps } from '../../ExportReportPdfModal/ExportVisualizationContainer';
import { selectIsCategorizedByNone } from '../../selectors/currentCategorizedBySelectors';
import {
  selectChartCalculations,
  selectChartCalculationsForExport,
  selectColorMap,
  selectColumnFormatter,
  selectExcludedCategorizedByValues,
  selectIsPercentOfTotal,
  selectLabels,
  selectNumberOfCategories,
  selectTargetStatuses,
  selectTooltipTickLabelFormatter,
  selectVisualizedCategorizedByColumn,
  selectVisualizedChartType,
  selectVisualizedPresentationConfiguration,
  selectVisualizedReportConfiguration
} from '../../selectors/selectReportExplorerState';
import { IChartProps } from '../../types';
import { customColorFor } from '../customColorUtils';
import { IReportVisualizationProps } from '../ReportVisualization';
import { chartActionCreators } from '../state';
import { targetColorMap } from '../targetColorUtils';

import {
  getChartContainerRect,
  getSelectedCategory,
  getSelectedElements,
  getSelectedPoint,
  getTooltip,
  selectCategorizedByColumnForChart,
  selectHaveDataToDisplay
} from './chartSelectors';
import { selectReportVisualizationPropsHandlers } from './selectReportVisualizationPropsHandlers';
import { IGraphCallbackCategoryData } from './selectReportVisualizationPropsUtils';

export type ILineGraphCallbackData = {
  category: string;
  id: string;
  value: number;
  x: number;
  y0: number;
  y1: number;
  data: IGraphCallbackCategoryData;
};

export const selectLegendSortOptions = createSelector(reportLocaleText, (localeText) => [
  {
    value: LegendSortOptions.LargestToSmallest,
    label: localeText.legend.sortOrderDesc
  },
  {
    value: LegendSortOptions.SmallestToLargest,
    label: localeText.legend.sortOrderAsc
  },
  {
    value: LegendSortOptions.Asc,
    label: localeText.legend.sortOrderAz
  },
  {
    value: LegendSortOptions.Desc,
    label: localeText.legend.sortOrderZa // pizza?
  }
]);

// the query will return the % change for the previous point even if
// there are gaps.  This allows us to not show a % change from a gap
export const hasPreviousData =
  (allData: [{ id }]) =>
  (id, category: string): boolean => {
    // sometimes id is a string ... sometimes
    const currentIdx = allData.findIndex((d) => `${d.id}` === `${id}`);
    if (currentIdx === 0) {
      return false;
    }

    return Object.prototype.hasOwnProperty.call(allData[currentIdx - 1], category);
  };

export const selectTooltip = createSelector(
  getTooltip,
  getSelectedCategory,
  selectPresentationConfiguration,
  (tooltip, selectedCategory, presentationConfiguration) => {
    const customColorForSelectedCategory = customColorFor(presentationConfiguration, selectedCategory);

    return produce(tooltip, (draft) => {
      if (customColorForSelectedCategory !== null && tooltip.data && tooltip.data.categoryColor) {
        draft.data.categoryColor = customColorForSelectedCategory;
      }
    });
  }
);

export const selectIsCategorizedByTargetStatus = createSelector(
  selectCategorizedByColumnForChart,
  (categorizedByColumn) => categorizedByColumn?.columnType === EReportColumnTypes.targetStatus
);

const transformChartCalcsForTargetStatus = (chartCalcs) => {
  const data = chartCalcs.data.map((item) => {
    const categories = chartCalcs.categories;
    const itemCopy = { ...item };
    const keys = Object.keys(itemCopy);
    let matchingCategory = '';

    if (intersection(keys, categories).length) {
      matchingCategory = intersection(keys, categories)[0];
      itemCopy[OG_TARGET_CATEGORY] = itemCopy[matchingCategory];
      return itemCopy;
    } else {
      return item;
    }
  });

  return {
    ...chartCalcs,
    data,
    categories: [OG_TARGET_CATEGORY]
  };
};

const getChartProps = ({ chartType, visualizationDetails, chartCalculations, isCategorizedByTargetStatus }) => {
  switch (chartType) {
    default:
    case EReportChartType.VerticalBar:
      return {
        ...visualizationDetails,
        chartCalculations,
        chartType: EReportChartType.VerticalBar
      };
    case EReportChartType.HorizontalBar:
      return {
        ...visualizationDetails,
        chartCalculations,
        chartType: EReportChartType.HorizontalBar
      };
    case EReportChartType.Line:
      return {
        ...visualizationDetails,
        chartCalculations: isCategorizedByTargetStatus
          ? transformChartCalcsForTargetStatus(chartCalculations)
          : chartCalculations,
        chartType: EReportChartType.Line
      };
    case EReportChartType.Pie:
      return {
        ...visualizationDetails,
        chartCalculations,
        chartType: EReportChartType.Pie
      };
  }
};

export const selectChartProps = createSelector(
  selectVisualizedChartType,
  selectLabels,
  selectChartCalculations,
  selectIsCategorizedByTargetStatus,
  (chartType, visualizationDetails, chartCalculations, isCategorizedByTargetStatus): IChartProps => {
    return getChartProps({ chartType, visualizationDetails, chartCalculations, isCategorizedByTargetStatus });
  }
);

export const selectChartPropsForExport = createSelector(
  selectVisualizedChartType,
  selectLabels,
  selectChartCalculationsForExport,
  selectIsCategorizedByTargetStatus,
  (chartType, visualizationDetails, chartCalculations, isCategorizedByTargetStatus): IChartProps => {
    return getChartProps({ chartType, visualizationDetails, chartCalculations, isCategorizedByTargetStatus });
  }
);

export const selectLegendListHeight = createSelector(getChartContainerRect, (rect) => {
  const heightOfLegendSelect = 44;
  const heightOfShadow = 8;
  return rect ? rect.height - heightOfLegendSelect - heightOfShadow : undefined;
});

export const selectTargetValueLocaleFormatter = createSelector(
  selectTarget,
  selectLocaleFormatter,
  (target, localeFormatter) => {
    if (!target || !target.conditions.length) {
      return localeFormatter.number;
    }
    const condition = target.conditions[0];
    switch (condition.value.valueType) {
      case EReportTargetValueTypes.currency:
        return localeFormatter.currency;
      case EReportTargetValueTypes.percent:
        return localeFormatter.percent;
      default:
        return localeFormatter.number;
    }
  }
);

export const selectTargetValueFormatter = createSelector(
  selectTargetValueLocaleFormatter,
  (localeFormatter) => (value: number | string) => localeFormatter({ value: parseFloat(value.toString()) })
);

export const selectLegendColorMap = createSelector(
  selectChartCalculations,
  selectIsCategorizedByTargetStatus,
  selectTarget,
  (chartCalcs, isCategorizedByTargetStatus, target) =>
    isCategorizedByTargetStatus ? <any>targetColorMap(target) : chartCalcs.colorMap
);

const updateExcludedCategorizedByValues = (category: string) => {
  reportConfigurationActionCreators.toggleVisualizationQueryExcludedCategorizedByValue(category);
  reportConfigurationEffects.createReportConfigurationAndUpdateUrl();
};

export const selectUpdateSelectedChartCategory = createSelector(
  selectSavedViewEditActiveTabIndex,
  selectColorMap,
  selectIsCategorizedByNone,
  selectNumberOfCategories,
  (activeTabIndex, colorMap, isNone, numberOfCategories) => (columnName, category) => {
    selectReportVisualizationPropsHandlers.updateSelectedChartCategory(
      reportConfigurationActionCreators,
      reportConfigurationEffects
    )(columnName, category, activeTabIndex, colorMap, isNone, numberOfCategories);
  }
);

export const selectUpdateSelectedCategory = createSelector(
  selectChartCalculations,
  selectReportVisualizationPropsHandlers.updateSelectedCategory(chartActionCreators)
);

export const selectElementColorFor = createSelector(
  selectVisualizedChartType,
  getSelectedElements,
  getSelectedCategory,
  selectChartCalculations,
  selectExcludedCategorizedByValues,
  selectIsCategorizedByTargetStatus,
  selectTarget,
  (
    chartType,
    selectedElements,
    selectedCategory,
    chartCalcs,
    excludedCategories,
    isCategorizedByTargetStatus,
    target
  ) => {
    const colorMap = isCategorizedByTargetStatus ? targetColorMap(target) : chartCalcs && chartCalcs.colorMap;

    if (chartType === EReportChartType.Pie) {
      return selectReportVisualizationPropsHandlers.elementColorForPie(selectedCategory, colorMap, excludedCategories);
    }
    return selectReportVisualizationPropsHandlers.elementColorFor(selectedCategory, selectedElements, colorMap);
  }
);

export const selectElementColorForLine = createSelector(
  selectChartCalculations,
  selectIsCategorizedByTargetStatus,
  selectTarget,
  (chartCalcs, isCategorizedByTargetStatus, target) =>
    selectReportVisualizationPropsHandlers.elementColorForLine(
      isCategorizedByTargetStatus ? targetColorMap(target) : chartCalcs && chartCalcs.colorMap
    )
);

export const selectElementColorForInnerPoint = createSelector(
  getSelectedPoint,
  getSelectedElements,
  selectChartCalculations,
  selectIsCategorizedByTargetStatus,
  selectTarget,
  (selectedPoint, selectedElements, chartCalcs, isCategorizedByTargetStatus, target) => {
    if (isCategorizedByTargetStatus) {
      return selectReportVisualizationPropsHandlers.elementColorForInnerPointTarget(
        targetColorMap(target),
        selectedPoint,
        selectedElements
      );
    }

    return selectReportVisualizationPropsHandlers.elementColorForInnerPoint(
      chartCalcs && chartCalcs.colorMap,
      selectedPoint
    );
  }
);

export const selectElementColorForOuterPoint = createSelector(
  getSelectedPoint,
  getSelectedElements,
  selectChartCalculations,
  selectIsCategorizedByTargetStatus,
  selectTarget,
  (selectedPoint, selectedElements, chartCalcs, isCategorizedByTargetStatus, target) => {
    if (isCategorizedByTargetStatus) {
      return selectReportVisualizationPropsHandlers.elementColorForOuterPointTarget(
        targetColorMap(target),
        selectedPoint,
        selectedElements
      );
    }

    return selectReportVisualizationPropsHandlers.elementColorForOuterPoint(
      chartCalcs && chartCalcs.colorMap,
      selectedPoint
    );
  }
);

export const selectLegendProps = createSelector(
  selectLegendSortOrder,
  selectLegendListHeight,
  selectLegendSortOptions,
  reportLocaleText,
  selectChartCalculations,
  selectLegendColorMap,
  selectIsPercentOfTotal,
  selectVisualizedCategorizedByColumn,
  selectUpdateSelectedChartCategory,
  selectExcludedCategorizedByValues,
  selectUpdateSelectedCategory,
  (
    selectedValue,
    listHeight,
    sortOptions,
    reportText,
    chartCalcs,
    legendColorMap,
    isPercentOfTotal,
    categorizedByColumn,
    updateSelectedChartCategory,
    excludedCategorizedByValues,
    updateSelectedCategory
  ) => {
    const categorizedByColumnType = categorizedByColumn?.type;

    const options = chartCalcs.categories.map((category) => {
      let label = category === '' ? reportText.reportExplorer.emptyValues : category;

      if (categorizedByColumnType === BasicColumnTypes.Boolean) {
        label = startCase(category);
      }

      const legendItemColors = legendColorMap ? legendColorMap[category] : chartCalcs.colorMap[category];

      return {
        color: legendItemColors && legendItemColors.base,
        hoverColor: legendItemColors && legendItemColors.highlight,
        label,
        value: category,
        ariaLabel: `Filter for ${category}`
      };
    });

    const selectedOptions = chartCalcs.categories
      .map((category) => {
        if (excludedCategorizedByValues && excludedCategorizedByValues.includes(category)) {
          return null;
        } else {
          return category;
        }
      })
      .filter((i) => !!i);

    return {
      listHeight,
      sortValue: selectedValue,
      sortOptions,
      onSortChange: (option) => {
        reportConfigurationActionCreators.setLegendSortOrder(option);
        reportConfigurationEffects.createReportConfigurationAndUpdateUrl();
      },
      options,
      selectedOptions: new Set(selectedOptions),
      onChange: (option) => {
        if (isPercentOfTotal) {
          updateExcludedCategorizedByValues(option.value);
        } else {
          updateSelectedChartCategory(categorizedByColumn.name, option.value);
        }
      },
      onOptionMouseEnter: (option) => {
        updateSelectedCategory(option.value);
      },
      onOptionMouseLeave: () => {
        updateSelectedCategory(null);
      }
    };
  }
);

export const selectShowCategoriesHiddenOverlay = createSelector(
  selectExcludedCategorizedByValues,
  selectChartCalculations,
  (excludedCategorizedByValues, chartCalculations) =>
    excludedCategorizedByValues &&
    excludedCategorizedByValues.length > 0 &&
    chartCalculations &&
    chartCalculations.categories.length > 0 &&
    excludedCategorizedByValues.length === chartCalculations.categories.length
);

export const selectReportVisualizationProps = createStructuredSelector<any, IReportVisualizationProps>({
  categorizedByColumn: selectVisualizedCategorizedByColumn,
  chartProps: selectChartProps,
  currentIsPercentOfTotal: selectIsPercentOfTotal,
  customColorMap: selectCustomColorMap,
  displayChart: selectHaveDataToDisplay,
  elementColorFor: selectElementColorFor,
  elementColorForLine: selectElementColorForLine,
  elementColorForInnerPoint: selectElementColorForInnerPoint,
  elementColorForOuterPoint: selectElementColorForOuterPoint,
  excludedCategorizedByValues: selectExcludedCategorizedByValues,
  legendGroupAriaLabel: createSelector(
    selectVisualizedCategorizedByColumn,
    (categorizedByColumn) => `Categories grouped by ${categorizedByColumn?.displayName}`
  ),
  legendProps: selectLegendProps,
  onElementEnter: createSelector(
    selectChartCalculations,
    selectVisualizedChartType,
    selectTooltip,
    selectTooltipTickLabelFormatter,
    selectVisualizedReportConfiguration,
    selectLocalePercentFormat,
    selectColumnFormatter,
    reportLocaleText,
    selectExcludedCategorizedByValues,
    selectIsCategorizedByTargetStatus,
    selectTarget,
    (
      calcs,
      chartType,
      tooltip,
      tickFormatter,
      reportConfiguration,
      percentFormatter,
      formatter,
      reportText,
      excludedCategories,
      isCategorizedByTargetStatus,
      target
    ) => {
      // tslint:disable-next-line:switch-default
      switch (chartType) {
        case EReportChartType.Line:
          return selectReportVisualizationPropsHandlers.onElementEnterLine(chartActionCreators)(
            calcs.data,
            tooltip,
            isCategorizedByTargetStatus ? targetColorMap(target) : calcs.colorMap,
            tickFormatter,
            percentFormatter,
            formatter,
            reportConfiguration,
            reportText.reportExplorer.emptyValues,
            reportText.reportExplorer.notApplicable
          );
        case EReportChartType.VerticalBar:
        case EReportChartType.HorizontalBar:
          return selectReportVisualizationPropsHandlers.onElementEnterStack(chartActionCreators)(
            calcs.data,
            tooltip,
            isCategorizedByTargetStatus ? targetColorMap(target) : calcs.colorMap,
            tickFormatter,
            percentFormatter,
            formatter,
            reportConfiguration,
            reportText.reportExplorer.emptyValues,
            reportText.reportExplorer.notApplicable
          );
        case EReportChartType.Pie:
          return selectReportVisualizationPropsHandlers.onElementEnterPie(chartActionCreators)(
            tooltip,
            calcs.colorMap,
            calcs.showAsPercentOfTotal,
            tickFormatter,
            percentFormatter,
            formatter,
            reportConfiguration,
            excludedCategories,
            reportText.reportExplorer.emptyValues
          );
        default:
          return null;
      }
    }
  ),
  onElementLeave: createSelector(
    selectTooltip,
    selectReportVisualizationPropsHandlers.onElementLeave(chartActionCreators)
  ),
  onMouseLeaveTooltip: createSelector(
    selectTooltip,
    selectReportVisualizationPropsHandlers.onMouseLeaveTooltip(chartActionCreators)
  ),
  presentationConfiguration: selectVisualizedPresentationConfiguration,
  reportChartContainerBoundingClientRect: () => (rect: IRectangle) => {
    chartActionCreators.setChartContainerRect(rect);
  },
  reportText: reportLocaleText,
  reportTooltipBoundingClientRect: createSelector(
    selectVisualizedChartType,
    selectTooltip,
    getChartContainerRect,
    selectReportVisualizationPropsHandlers.reportTooltipBoundingClientRect(chartActionCreators)
  ),
  selectedCategory: getSelectedCategory,
  selectedPoint: getSelectedPoint,
  showCategoriesHiddenOverlay: createSelector(
    selectExcludedCategorizedByValues,
    selectChartCalculations,
    (excludedCategorizedByValues, chartCalculations) =>
      excludedCategorizedByValues &&
      excludedCategorizedByValues.length > 0 &&
      chartCalculations &&
      chartCalculations.categories.length > 0 &&
      excludedCategorizedByValues.length === chartCalculations.categories.length
  ),
  targetStatuses: selectTargetStatuses,
  targetValueFormatter: selectTargetValueFormatter,
  tooltip: selectTooltip,
  updateChartContainerWidth: () => (rect) => chartActionCreators.setContainerWidth(rect.width),
  updateLegendSort: () => (option) => {
    {
      reportConfigurationActionCreators.setLegendSortOrder(option);
      reportConfigurationEffects.createReportConfigurationAndUpdateUrl();
    }
  },
  updateSelectedCategory: selectUpdateSelectedCategory,
  updateSelectedChartCategory: selectUpdateSelectedChartCategory,
  updateSelectedPoint: () => (point) => chartActionCreators.setSelectedPoint(point)
});

export function getReportVisualizationSelector(canUseHighcharts: boolean, state: any) {
  if (canUseHighcharts) {
    return undefined;
  }
  return selectReportVisualizationProps(state);
}

export const selectReportExportVisualizationProps = createStructuredSelector<any, IExportVisualizationContainerProps>({
  chartProps: selectChartPropsForExport,
  customColorMap: selectCustomColorMap,
  elementColorFor: selectElementColorFor,
  elementColorForLine: selectElementColorForLine,
  elementColorForInnerPoint: selectElementColorForInnerPoint,
  elementColorForOuterPoint: selectElementColorForOuterPoint,
  excludedCategorizedByValues: selectExcludedCategorizedByValues,
  legendOptions: createSelector(selectLegendProps, (props) => props.options),
  presentationConfiguration: selectVisualizedPresentationConfiguration,
  reportText: reportLocaleText,
  showCategoriesHiddenOverlay: selectShowCategoriesHiddenOverlay,
  targetStatuses: selectTargetStatuses,
  targetValueFormatter: selectTargetValueFormatter
});
